import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService, projectCanvasService } from '../services/database/projects';
import { 
  ArrowLeft, 
  ChevronRight, 
  ChevronLeft,
  MessageSquare,
  Lightbulb,
  Target,
  Layers,
  Zap,
  Users,
  DollarSign,
  Timer,
  Rocket,
  Plus,
  Trash2,
  Save,
  PenSquare,
  Check,
  User,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { generateBoltPrompt } from '../services/ai/deepseek';

type TabType = 'chat' | 'mvp' | 'canvas' | 'analysis';

interface Feature {
  id: string;
  description: string;
  priority: 'must-have' | 'nice-to-have';
  completed: boolean;
  createdAt: string;
}

interface MVPScope {
  features: Feature[];
  timeline: string;
  estimated_cost: number;
}

interface Message {
  content: string;
  timestamp: string;
  isUser: boolean;
}

export default function ProjectCanvasPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [canvas, setCanvas] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('chat');
  const [newFeature, setNewFeature] = useState('');
  const [notes, setNotes] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [timeline, setTimeline] = useState('');
  const [rawEstimatedCost, setRawEstimatedCost] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [successMetrics, setSuccessMetrics] = useState<string[]>([]);
  const [projectTitle, setProjectTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [canvasData, setCanvasData] = useState<{
    value_proposition: string;
    problem_solution: string;
    key_features: string[];
  }>({
    value_proposition: '',
    problem_solution: '',
    key_features: []
  });
  const [analysisData, setAnalysisData] = useState<{
    technical_feasibility: string;
    risks_and_mitigations: string;
    market_opportunities: string;
    suggested_technologies: string;
    resource_estimates: string;
    similar_projects: string;
    bolt_prompt: string;
  }>({
    technical_feasibility: '',
    risks_and_mitigations: '',
    market_opportunities: '',
    suggested_technologies: '',
    resource_estimates: '',
    similar_projects: '',
    bolt_prompt: ''
  });

  const [savingFeatures, setSavingFeatures] = useState<string[]>([]);
  const [errors, setErrors] = useState<{
    targetAudience?: string;
    timeline?: string;
    estimatedCost?: string;
  }>({});
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);

  const debounceSave = (updates: any) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    
    const timeout = setTimeout(async () => {
      try {
        await projectCanvasService.updateCanvas(projectId!, updates);
      } catch (error) {
        console.error('Erro ao salvar:', error);
      }
    }, 1000); // Espera 1 segundo após a última digitação
    
    setDebounceTimeout(timeout);
  };

  const handleTargetAudienceChange = (value: string) => {
    setTargetAudience(value);
    if (errors.targetAudience) {
      setErrors(prev => ({ ...prev, targetAudience: undefined }));
    }
    
    debounceSave({ target_audience: value });
  };

  const handleTimelineChange = (value: string) => {
    setTimeline(value);
    if (errors.timeline) {
      setErrors(prev => ({ ...prev, timeline: undefined }));
    }
    
    debounceSave({ 
      mvp_scope: {
        ...canvas?.mvp_scope,
        timeline: value
      }
    });
  };

  const handleEstimatedCostChange = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    setRawEstimatedCost(numbers);
    
    if (errors.estimatedCost) {
      setErrors(prev => ({ ...prev, estimatedCost: undefined }));
    }
    
    debounceSave({ 
      mvp_scope: {
        ...canvas?.mvp_scope,
        estimated_cost: parseInt(numbers) || 0
      }
    });
  };

  const handleAddFeature = async () => {
    if (!newFeature.trim()) return;

    const feature: Feature = {
      id: crypto.randomUUID(),
      description: newFeature,
      priority: 'must-have',
      completed: false,
      createdAt: new Date().toISOString()
    };

    const updatedFeatures = [...features, feature];
    setFeatures(updatedFeatures);
    setNewFeature('');

    debounceSave({ 
      mvp_scope: {
        ...canvas?.mvp_scope,
        features: updatedFeatures
      }
    });
  };

  const handleToggleFeature = async (featureId: string) => {
    try {
      setSavingFeatures(prev => [...prev, featureId]);
      
      const updatedFeatures = features.map(f =>
        f.id === featureId ? { ...f, completed: !f.completed } : f
      );
      
      setFeatures(updatedFeatures);
      
      debounceSave({ 
        mvp_scope: {
          ...canvas?.mvp_scope,
          features: updatedFeatures
        }
      });
    } finally {
      setSavingFeatures(prev => prev.filter(id => id !== featureId));
    }
  };

  const handleUpdateMetric = (index: number, value: string) => {
    const updatedMetrics = [...successMetrics];
    updatedMetrics[index] = value;
    setSuccessMetrics(updatedMetrics);
    
    debounceSave({ success_metrics: updatedMetrics });
  };

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  useEffect(() => {
    if (project?.title) {
      setProjectTitle(project.title);
    }
  }, [project]);

  useEffect(() => {
    if (canvas) {
      setTargetAudience(canvas.target_audience || '');
      setTimeline(canvas.mvp_scope?.timeline || '');
      const cost = canvas.mvp_scope?.estimated_cost || 0;
      setEstimatedCost(formatCurrency(cost.toString()));
      setRawEstimatedCost(cost.toString());
      setFeatures(canvas.mvp_scope?.features || []);
      setSuccessMetrics(canvas.success_metrics || []);
      setCanvasData(canvas.canvas || {
        value_proposition: '',
        problem_solution: '',
        key_features: []
      });
      
      setAnalysisData(canvas.analysis || {
        technical_feasibility: '',
        risks_and_mitigations: '',
        market_opportunities: '',
        suggested_technologies: '',
        resource_estimates: '',
        similar_projects: '',
        bolt_prompt: ''
      });
    }
  }, [canvas]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [projectData, canvasData] = await Promise.all([
        projectService.getProjectWithChat(projectId!),
        projectCanvasService.getCanvas(projectId!)
      ]);
      
      console.log('Project Data:', projectData); // Debug
      setProject({
        ...projectData,
        chat: projectData.chat?.[0] || { messages: [] }
      });
      setCanvas(canvasData);
      setMessages(projectData.chat?.[0]?.messages || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do projeto');
    } finally {
      setIsLoading(false);
    }
  };

  const validateFields = () => {
    const newErrors: typeof errors = {};
    
    if (!targetAudience.trim()) {
      newErrors.targetAudience = 'Público-alvo é obrigatório';
    }
    
    if (!timeline.trim()) {
      newErrors.timeline = 'Tempo de desenvolvimento é obrigatório';
    }
    
    if (!rawEstimatedCost.trim()) {
      newErrors.estimatedCost = 'Custo estimado é obrigatório';
    } else if (isNaN(parseFloat(rawEstimatedCost))) {
      newErrors.estimatedCost = 'Custo estimado deve ser um número';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatCurrency = (value: string) => {
    const number = parseInt(value) || 0;
    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number / 100);
    return formatted;
  };

  const handleEstimatedCostBlur = () => {
    // Formata o valor quando o campo perde o foco
    const formatted = formatCurrency(rawEstimatedCost);
    setEstimatedCost(formatted);
  };

  const handleRemoveFeature = (featureId: string) => {
    setFeatures(prev => prev.filter(f => f.id !== featureId));
  };

  const handleAddMetric = () => {
    setSuccessMetrics(prev => [...prev, '']);
  };

  const handleRemoveMetric = (index: number) => {
    setSuccessMetrics(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveMVP = async (currentFeatures = features) => {
    if (!validateFields()) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      setIsSaving(true);
      const updates = {
        target_audience: targetAudience.trim(),
        mvp_scope: {
          features: currentFeatures,
          timeline: timeline.trim(),
          estimated_cost: parseInt(rawEstimatedCost) || 0
        },
        success_metrics: successMetrics.filter(Boolean).map(m => m.trim())
      };

      await projectCanvasService.updateCanvas(projectId!, updates);
      toast.success('MVP atualizado com sucesso!');
      setErrors({});
    } catch (error) {
      console.error('Erro ao salvar MVP:', error);
      toast.error('Erro ao salvar MVP');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!projectTitle.trim()) return;

    try {
      await projectService.updateProject(projectId!, {
        title: projectTitle
      });
      setIsEditingTitle(false);
      toast.success('Título atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar título:', error);
      toast.error('Erro ao atualizar título');
    }
  };

  const handleSaveCanvas = async () => {
    try {
      setIsSaving(true);
      await projectCanvasService.updateCanvas(projectId!, {
        value_proposition: canvasData.value_proposition,
        problem_solution: canvasData.problem_solution,
        key_features: canvasData.key_features || []
      });
      toast.success('Canvas atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar canvas:', error);
      toast.error('Erro ao salvar canvas');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAnalysis = async () => {
    try {
      setIsSaving(true);
      await projectCanvasService.updateCanvas(projectId!, {
        ai_analysis: analysisData.bolt_prompt || ''
      });
      toast.success('Análise atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar análise:', error);
      toast.error('Erro ao salvar análise');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateAnalysis = async () => {
    try {
      setIsSaving(true);
      // Aqui você pode integrar com uma API de IA para gerar a análise
      // Por enquanto, vamos apenas salvar os dados
      await projectCanvasService.updateCanvas(projectId!, {
        analysis: analysisData
      });
      toast.success('Análise atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar análise:', error);
      toast.error('Erro ao atualizar análise');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePrompt = async () => {
    try {
      setIsSaving(true);
      
      // Primeiro salvamos os dados atuais
      await handleSaveMVP();

      // Construímos o contexto para a IA
      const context = {
        projectTitle: projectTitle,
        businessIdea: project?.project?.business_idea || '',
        targetAudience,
        features: features.map(f => ({
          description: f.description,
          priority: f.priority,
          completed: f.completed
        })),
        timeline,
        estimatedCost: rawEstimatedCost,
        successMetrics: successMetrics.filter(Boolean)
      };

      // Chamada para a API da Deepseek
      const prompt = await generateBoltPrompt(context);
      
      // Atualiza o prompt no banco
      await projectCanvasService.updateCanvas(projectId!, {
        bolt_prompt: prompt
      });

      toast.success('Prompt gerado com sucesso!');
      
      // Recarrega os dados para mostrar o prompt atualizado
      await loadData();
    } catch (error) {
      console.error('Erro ao gerar prompt:', error);
      toast.error('Erro ao gerar prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const message: Message = {
      content: inputMessage,
      timestamp: new Date().toISOString(),
      isUser: true
    };

    setMessages(prev => [...prev, message]);
    setInputMessage('');

    try {
      await projectService.sendMessage(projectId!, message);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    }
  };

  const tabs: { id: TabType; label: string; icon: any }[] = [
    { id: 'chat' as TabType, label: 'Chat', icon: MessageSquare },
    { id: 'mvp' as TabType, label: 'MVP Builder', icon: Rocket },
    { id: 'canvas' as TabType, label: 'Canvas', icon: Layers },
    { id: 'analysis' as TabType, label: 'Análise', icon: Lightbulb }
  ];

  interface MVPInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    multiline?: boolean;
    error?: string;
  }

  const MVPInput: React.FC<MVPInputProps> = ({ 
    value, 
    onChange, 
    placeholder, 
    className = '', 
    multiline = false,
    error
  }) => {
    if (multiline) {
      return (
        <div>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-white/[0.08] rounded-lg p-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/[0.16] ${error ? 'border-red-500' : ''} ${className}`}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
      );
    }

    return (
      <div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/[0.16] ${error ? 'border-red-500' : ''} ${className}`}
        />
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  };

  const handleCanvasChange = (field: keyof typeof canvasData, value: string | string[]) => {
    setCanvasData(prev => ({ ...prev, [field]: value }));
  };

  const handleAnalysisChange = (field: keyof typeof analysisData, value: string) => {
    setAnalysisData(prev => ({ ...prev, [field]: value }));
  };

  const CanvasSection = ({ title, value, onChange }: { title: string; value: string; onChange: (value: string) => void }) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      e.preventDefault();
      onChange(e.target.value);
    };

    return (
      <div className="bg-[#111]/80 backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-6">
        <h3 className="text-lg font-medium mb-4">{title}</h3>
        <textarea
          value={value}
          onChange={handleChange}
          className="w-full h-32 bg-white/[0.08] rounded-lg p-3 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
          placeholder={`Descreva ${title.toLowerCase()}...`}
        />
      </div>
    );
  };

  const MVPSection = ({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) => (
    <div className="bg-[#111]/80 backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-white/[0.08] rounded-lg">
          <Icon size={20} className="text-gray-400" />
        </div>
        <h3 className="text-lg font-medium">{title}</h3>
      </div>
      {children}
    </div>
  );

  const AnalysisSection = ({ title, content }: { title: string; content: string }) => (
    <div className="bg-[#111]/80 backdrop-blur-xl border border-white/[0.08] rounded-[20px] p-6 mb-6">
      <h3 className="text-lg font-medium mb-4">{title}</h3>
      <div className="text-sm text-gray-400 whitespace-pre-wrap">
        {content || 'Análise em desenvolvimento...'}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'chat':
        return (
          <div className="flex gap-6 h-[calc(100vh-240px)]">
            {/* Chat principal */}
            <div className="flex-1 flex flex-col">
              <div className="flex-1 space-y-6 overflow-y-auto pr-4 custom-scrollbar">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.isUser ? 'justify-start' : 'justify-end'}`}
                  >
                    {message.isUser && (
                      <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center mr-3 mt-1 shrink-0">
                        <User size={16} className="text-white" />
                      </div>
                    )}
                    <div
                      className={`group relative max-w-[80%] ${
                        message.isUser
                          ? 'bg-white text-black'
                          : 'bg-[rgb(28,28,30)] border border-[rgb(44,44,46)] text-white'
                      } rounded-2xl p-4 hover:bg-[rgb(44,44,46)] transition-all duration-200`}
                    >
                      <p className="text-[17px] leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
                      <p className={`text-[13px] ${message.isUser ? 'text-black/60' : 'text-[rgb(152,152,157)]'} mt-2`}>
                        {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    {!message.isUser && (
                      <div className="w-8 h-8 rounded-xl bg-blue-500/10 flex items-center justify-center ml-3 mt-1 shrink-0">
                        <MessageSquare size={16} className="text-blue-500" />
                      </div>
                    )}
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center space-y-3">
                      <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto">
                        <MessageSquare size={24} className="text-blue-500" />
                      </div>
                      <p className="text-[17px] text-white">Nenhuma mensagem ainda</p>
                      <p className="text-[rgb(152,152,157)] text-sm">Comece uma conversa digitando abaixo</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Input de mensagem */}
              <div className="mt-6">
                <div className="relative">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    placeholder="Digite sua mensagem..."
                    className="w-full bg-[rgb(28,28,30)] border border-[rgb(44,44,46)] hover:border-[rgb(44,44,46)]/80 focus:border-white/20 rounded-2xl pl-4 pr-12 py-3 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none transition-all duration-200"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-white/90 text-black rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={18} className={!inputMessage.trim() ? 'opacity-50' : ''} />
                  </button>
                </div>
              </div>
            </div>

            {/* Informações do Lead */}
            <div className="w-80 shrink-0">
              <div className="bg-[rgb(28,28,30)] border border-[rgb(44,44,46)] rounded-2xl p-6 sticky top-6 hover:bg-[rgb(44,44,46)] transition-all duration-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <User size={24} className="text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-4">Informações do Lead</h3>
                    <p className="text-[13px] text-[rgb(152,152,157)]">Detalhes do contato</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <p className="text-[13px] text-[rgb(152,152,157)] mb-1">Nome</p>
                    <p className="text-[17px] text-white">{project?.lead?.name}</p>
                  </div>
                  
                  <div>
                    <p className="text-[13px] text-[rgb(152,152,157)] mb-1">Email</p>
                    <p className="text-[17px] text-white break-all">{project?.lead?.email}</p>
                  </div>
                  
                  <div>
                    <p className="text-[13px] text-[rgb(152,152,157)] mb-1">Telefone</p>
                    <p className="text-[17px] text-white">{project?.lead?.phone}</p>
                  </div>
                  
                  <div>
                    <p className="text-[13px] text-[rgb(152,152,157)] mb-1">Capturado em</p>
                    <p className="text-[17px] text-white">
                      {new Date(project?.lead?.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'mvp':
        return (
          <div>
            <div className="grid grid-cols-2 gap-6">
              {/* Funcionalidades */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Zap size={20} className="text-[rgb(152,152,157)]" />
                    <h3 className="text-[20px] font-semibold text-white">Principais Funcionalidades</h3>
                  </div>
                </div>

                <div className="space-y-3">
                  {features.map((feature) => (
                    <div
                      key={feature.id}
                      className="flex items-start gap-3 p-3 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl hover:bg-[rgb(44,44,46)] transition-all duration-200"
                    >
                      <button
                        onClick={() => handleToggleFeature(feature.id)}
                        className={`shrink-0 w-5 h-5 rounded-md border ${
                          feature.completed
                            ? 'bg-white border-white'
                            : 'border-[rgb(152,152,157)] hover:border-white'
                        } transition-all duration-200 flex items-center justify-center`}
                      >
                        {feature.completed && <Check size={12} className="text-black" />}
                      </button>
                      <p className="text-[17px] text-white">{feature.description}</p>
                    </div>
                  ))}

                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddFeature()}
                      placeholder="Adicionar funcionalidade..."
                      className="flex-1 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl px-4 py-2 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20"
                    />
                    <button
                      onClick={handleAddFeature}
                      className="p-2 bg-white hover:bg-white/90 text-black rounded-xl transition-all duration-200"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Público-Alvo */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Users size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Público-Alvo</h3>
                </div>
                <textarea
                  value={targetAudience}
                  onChange={(e) => handleTargetAudienceChange(e.target.value)}
                  placeholder="Descreva o público-alvo do MVP..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Estimativas */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Timer size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Estimativas</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-[13px] text-[rgb(152,152,157)] mb-2">Tempo de Desenvolvimento</p>
                    <input
                      type="text"
                      value={timeline}
                      onChange={(e) => handleTimelineChange(e.target.value)}
                      placeholder="Ex: 2 meses"
                      className="w-full bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl px-4 py-2 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20"
                    />
                  </div>

                  <div>
                    <p className="text-[13px] text-[rgb(152,152,157)] mb-2">Custo Estimado</p>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[17px] text-[rgb(152,152,157)]">R$</span>
                      <input
                        type="text"
                        value={rawEstimatedCost}
                        onChange={(e) => handleEstimatedCostChange(e.target.value)}
                        placeholder="0"
                        className="w-full bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl pl-10 pr-4 py-2 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Métricas de Sucesso */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Target size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Métricas de Sucesso</h3>
                </div>

                <div className="space-y-3">
                  {successMetrics.map((metric, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={metric}
                        onChange={(e) => handleUpdateMetric(index, e.target.value)}
                        placeholder="Defina uma métrica de sucesso..."
                        className="flex-1 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl px-4 py-2 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20"
                      />
                      <button
                        onClick={() => handleRemoveMetric(index)}
                        className="p-2 hover:bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:text-white rounded-xl transition-all duration-200"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}

                  <button
                    onClick={handleAddMetric}
                    className="w-full px-4 py-2 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)] hover:text-white transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Adicionar Métrica
                  </button>
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="mt-8 flex justify-end gap-4">
              <button
                onClick={() => handleSaveMVP()}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                {isSaving ? 'Salvando...' : 'Salvar MVP'}
              </button>
              
              <button
                onClick={handleGeneratePrompt}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Zap size={20} />
                {isSaving ? 'Gerando...' : 'Gerar Prompt'}
              </button>
            </div>
          </div>
        );

      case 'canvas':
        return (
          <div>
            <div className="grid grid-cols-2 gap-6">
              {/* Proposta de Valor */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Zap size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Proposta de Valor</h3>
                </div>
                <textarea
                  value={canvasData.value_proposition}
                  onChange={(e) => handleCanvasChange('value_proposition', e.target.value)}
                  placeholder="Descreva a proposta de valor do projeto..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Problema e Solução */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Target size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Problema e Solução</h3>
                </div>
                <textarea
                  value={canvasData.problem_solution}
                  onChange={(e) => handleCanvasChange('problem_solution', e.target.value)}
                  placeholder="Descreva o problema e a solução proposta..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Funcionalidades Principais */}
              <div className="col-span-2 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Layers size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Funcionalidades Principais</h3>
                </div>
                <div className="space-y-3">
                  {canvasData.key_features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [...canvasData.key_features];
                          newFeatures[index] = e.target.value;
                          handleCanvasChange('key_features', newFeatures);
                        }}
                        placeholder="Descreva uma funcionalidade principal..."
                        className="flex-1 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl px-4 py-2 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20"
                      />
                      <button
                        onClick={() => {
                          const newFeatures = canvasData.key_features.filter((_, i) => i !== index);
                          handleCanvasChange('key_features', newFeatures);
                        }}
                        className="p-2 hover:bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:text-white rounded-xl transition-all duration-200"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => handleCanvasChange('key_features', [...canvasData.key_features, ''])}
                    className="w-full px-4 py-2 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)] hover:text-white transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <Plus size={16} />
                    Adicionar Funcionalidade
                  </button>
                </div>
              </div>
            </div>

            {/* Botão de Salvar */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSaveCanvas}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        );

      case 'analysis':
        return (
          <div>
            <div className="grid grid-cols-2 gap-6">
              {/* Viabilidade Técnica */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Zap size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Viabilidade Técnica</h3>
                </div>
                <textarea
                  value={analysisData.technical_feasibility}
                  onChange={(e) => handleAnalysisChange('technical_feasibility', e.target.value)}
                  placeholder="Avalie a viabilidade técnica do projeto..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Riscos e Mitigações */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Target size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Riscos e Mitigações</h3>
                </div>
                <textarea
                  value={analysisData.risks_and_mitigations}
                  onChange={(e) => handleAnalysisChange('risks_and_mitigations', e.target.value)}
                  placeholder="Liste os principais riscos e suas mitigações..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Oportunidades de Mercado */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <DollarSign size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Oportunidades de Mercado</h3>
                </div>
                <textarea
                  value={analysisData.market_opportunities}
                  onChange={(e) => handleAnalysisChange('market_opportunities', e.target.value)}
                  placeholder="Descreva as oportunidades de mercado identificadas..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Tecnologias Sugeridas */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Layers size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Tecnologias Sugeridas</h3>
                </div>
                <textarea
                  value={analysisData.suggested_technologies}
                  onChange={(e) => handleAnalysisChange('suggested_technologies', e.target.value)}
                  placeholder="Liste as tecnologias recomendadas..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Estimativa de Recursos */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Users size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Estimativa de Recursos</h3>
                </div>
                <textarea
                  value={analysisData.resource_estimates}
                  onChange={(e) => handleAnalysisChange('resource_estimates', e.target.value)}
                  placeholder="Detalhe os recursos necessários..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>

              {/* Projetos Similares */}
              <div className="bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Lightbulb size={20} className="text-[rgb(152,152,157)]" />
                  <h3 className="text-[20px] font-semibold text-white">Projetos Similares</h3>
                </div>
                <textarea
                  value={analysisData.similar_projects}
                  onChange={(e) => handleAnalysisChange('similar_projects', e.target.value)}
                  placeholder="Liste projetos similares no mercado..."
                  className="w-full h-32 bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl border border-[rgb(44,44,46)] rounded-xl p-4 text-[17px] text-white placeholder-[rgb(152,152,157)] focus:outline-none focus:border-white/20 resize-none"
                />
              </div>
            </div>

            {/* Botão de Atualizar */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSaveAnalysis}
                disabled={isSaving}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Atualizando...' : 'Atualizar Análise'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="space-y-8">
        {/* Header com navegação */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/projects')}
              className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-400" />
            </button>
            <div>
              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={projectTitle}
                    onChange={(e) => setProjectTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveTitle()}
                    className="text-2xl font-medium bg-white/[0.08] rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveTitle}
                    className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
                  >
                    <Save size={16} className="text-gray-400" />
                  </button>
                </div>
              ) : (
                <h1 
                  className="text-2xl font-medium cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={() => setIsEditingTitle(true)}
                >
                  {projectTitle}
                </h1>
              )}
              <p className="text-gray-400">Canvas do Projeto</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 flex items-center gap-2 pb-4">
          <button
            onClick={() => setActiveTab('chat')}
            className={`px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'chat'
                ? 'bg-white text-black'
                : 'bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)]'
            }`}
          >
            <MessageSquare size={16} />
            Chat
          </button>
          <button
            onClick={() => setActiveTab('mvp')}
            className={`px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'mvp'
                ? 'bg-white text-black'
                : 'bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)]'
            }`}
          >
            <Rocket size={16} />
            MVP
          </button>
          <button
            onClick={() => setActiveTab('canvas')}
            className={`px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'canvas'
                ? 'bg-white text-black'
                : 'bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)]'
            }`}
          >
            <Layers size={16} />
            Canvas
          </button>
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              activeTab === 'analysis'
                ? 'bg-white text-black'
                : 'bg-[rgb(28,28,30)]/[0.54] backdrop-blur-xl text-[rgb(152,152,157)] hover:bg-[rgb(44,44,46)]'
            }`}
          >
            <Lightbulb size={16} />
            Análise
          </button>
        </div>

        {/* Conteúdo */}
        <div className="pt-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
